/**
 * People (Figma 09 · People 01–16): the team and the workspace's clients.
 * Not built, no backend yet: Resend / Copy link, email-not-delivered,
 * Joined date, "Scheduled by" posts, handing posts over on remove, Ask for
 * more seats, declined and expired invitations, and invitation rows for
 * non-owners (that list is owner-only).
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { ChevronDown, Ellipsis, Hourglass, Search, Store, UserMinus, Users } from "lucide-react";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useGetBrands } from "@/hooks/useGetBrands";
import { useOrgSystemBudget } from "@/hooks/useOrgSystemBudget";
import { queue } from "@/components/Toast";
import {
  useOrganizationInvites,
  useOrganizationMembers,
  useRemoveMember,
  useRevokeInvite,
  useUpdateMemberRole,
} from "@/app/routes/dashboard/settings/api/useOrganization";
import type {
  AssignableRole,
  BrandRef,
  OrganizationInviteSummary,
  OrganizationMember,
} from "@/types/organization.type";
import { KitButton } from "../../kit/Button";
import { KitIconButton } from "../../kit/IconButton";
import { KitInput } from "../../kit/Input";
import { KitMenu, KitMenuItem, KitMenuPopover, KitMenuTrigger, KitPressable } from "../../kit/Menu";
import { BrandMark } from "../../kit/BrandPicker";
import { ErrorState } from "../../ViewStates";
import {
  ConfirmDialog,
  DetailCard,
  DetailHead,
  Fact,
  Facts,
  GroupLabel,
  IndexCard,
  IndexRow,
  PageHead,
  PersonMark,
  QuietNote,
  SectionHead,
  SettingsPage,
  Split,
  SplitLoading,
  StatusLabel,
  TextAction,
} from "../parts/SettingsParts";
import { useWorkspaceHead } from "../parts/scope";
import { roleCan, roleLabel, takesSeat } from "../parts/roles";
import { EditBrandsDialog, InviteDialog } from "../parts/dialogs";
import { formatDay, formatMoment, plural } from "../parts/settingsFormat";

type Selection = { kind: "member"; id: string } | { kind: "invite"; email: string };

const RANK: Record<string, number> = { Guest: 0, Viewer: 1, Contributor: 2, Owner: 3 };
const ROLE_OPTIONS: AssignableRole[] = ["Contributor", "Viewer", "Guest"];

const firstName = (m: { name?: string | null; email: string }) => (m.name || m.email).split(/[\s@]/)[0];

function HourglassMark() {
  return (
    <span aria-hidden className="grid size-9 shrink-0 place-items-center rounded-full border border-co-line2 text-co-muted">
      <Hourglass className="size-4" />
    </span>
  );
}

function memberSub(m: OrganizationMember, meId: string | undefined): string {
  if (m.role === "Guest") {
    const brands = m.brands ?? [];
    return `Client · ${brands.length === 1 ? brands[0].name : plural(brands.length, "brand")}`;
  }
  return `${m.role}${m.userId === meId ? " · You" : ""}`;
}

function inviteSub(i: OrganizationInviteSummary): string {
  return `Invitation sent · expires ${formatDay(i.expiresAt)}`;
}

export default function PeoplePage() {
  const head = useWorkspaceHead();
  const { selectedOrganization, user } = useCurrentUser();
  const orgId = selectedOrganization?.organization.id ?? null;
  const orgName = selectedOrganization?.organization.name ?? "the workspace";
  const isOwner = selectedOrganization?.role === "Owner";
  const [searchParams, setSearchParams] = useSearchParams();

  const membersQuery = useOrganizationMembers(orgId);
  const invitesQuery = useOrganizationInvites(orgId, isOwner);
  const { data: budget } = useOrgSystemBudget();
  const { data: brands } = useGetBrands();
  const updateRole = useUpdateMemberRole(orgId);
  const removeMember = useRemoveMember(orgId);
  const revokeInvite = useRevokeInvite(orgId);

  const [picked, setPicked] = useState<Selection | null>(null);
  const [query, setQuery] = useState("");
  // Plan and seats' "Invite someone" lands here with ?invite=1.
  const [inviteOpen, setInviteOpen] = useState(() => searchParams.get("invite") === "1");
  const [widen, setWiden] = useState<{ member: OrganizationMember; role: AssignableRole } | null>(null);
  const [toClient, setToClient] = useState<OrganizationMember | null>(null);
  const [removeTarget, setRemoveTarget] = useState<OrganizationMember | null>(null);

  useEffect(() => {
    if (searchParams.get("invite") !== "1") return;
    const next = new URLSearchParams(searchParams);
    next.delete("invite");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const members = membersQuery.data ?? [];
  const invites = invitesQuery.data ?? [];
  const brandCount = brands?.length ?? 0;
  const seats = budget?.seats;
  const maxSeats = seats?.maxSeats ?? null;
  const usedSeats = seats ? seats.members + seats.pendingInvites : members.filter((m) => takesSeat(m.role)).length;
  const seatsLeft = maxSeats != null ? Math.max(0, maxSeats - usedSeats) : null;
  const owner = members.find((m) => m.role === "Owner");

  const q = query.trim().toLowerCase();
  const matches = (text: string) => !q || text.toLowerCase().includes(q);
  const team = members.filter((m) => m.role !== "Guest" && matches(`${m.name} ${m.email}`));
  const clients = members.filter((m) => m.role === "Guest" && matches(`${m.name} ${m.email}`));
  const teamInvites = invites.filter((i) => i.role !== "Guest" && matches(i.email));
  const clientInvites = invites.filter((i) => i.role === "Guest" && matches(i.email));
  const roster = members.length + invites.length;

  const firstMember = members.find((m) => m.role !== "Guest") ?? members[0];
  const fallback: Selection | null = firstMember ? { kind: "member", id: firstMember.userId } : null;
  const selectedMember =
    picked?.kind === "member" ? members.find((m) => m.userId === picked.id) : undefined;
  const selectedInvite =
    picked?.kind === "invite" ? invites.find((i) => i.email.toLowerCase() === picked.email) : undefined;
  const selection: Selection | null = selectedMember || selectedInvite ? picked : fallback;
  const shownMember =
    selectedMember ?? (selection?.kind === "member" ? members.find((m) => m.userId === selection.id) : undefined);

  /** Narrowing applies at once with Undo; widening asks first (People 09 / 10). */
  const changeRole = (member: OrganizationMember, role: AssignableRole, brandsFor?: BrandRef[]) => {
    if (role === member.role) return;
    if (role === "Guest" && !brandsFor) {
      setToClient(member);
      return;
    }
    if (RANK[role] > RANK[member.role]) {
      setWiden({ member, role });
      return;
    }
    const previous = { role: member.role as AssignableRole, brands: member.brands ?? undefined };
    updateRole.mutate(
      { userId: member.userId, role, brands: brandsFor, quiet: true },
      {
        onSuccess: () =>
          queue.add({
            title: `${member.name || member.email} is now ${role === "Guest" ? "a Client" : `a ${role}`}`,
            description:
              role === "Viewer"
                ? `${firstName(member)} can no longer schedule. Posts they scheduled stay as planned.`
                : role === "Guest"
                  ? `${firstName(member)} now sees only the brands you picked, and frees a seat.`
                  : undefined,
            variant: "success",
            action: {
              label: "Undo",
              onPress: () =>
                updateRole.mutate({ userId: member.userId, role: previous.role, brands: previous.brands ?? undefined }),
            },
            countdown: 6,
          }),
      },
    );
  };

  const status = membersQuery.isLoading
    ? `Reading who is in ${orgName}.`
    : isOwner
    ? maxSeats != null
      ? `${usedSeats} of ${maxSeats} seats in use. Clients do not take a seat.`
      : `${plural(usedSeats, "seat")} in use. Clients do not take a seat.`
    : `Only owners invite people or change roles.${owner ? ` Ask ${owner.name || owner.email}.` : ""}`;

  const memberRow = (m: OrganizationMember) => (
    <IndexRow
      key={m.userId}
      lead={<PersonMark name={m.name || m.email} picture={m.profilePicture} />}
      title={m.name || m.email}
      sub={memberSub(m, user?.id)}
      active={selection?.kind === "member" && selection.id === m.userId}
      onPress={() => setPicked({ kind: "member", id: m.userId })}
    />
  );
  const inviteRow = (i: OrganizationInviteSummary) => (
    <IndexRow
      key={i.id}
      lead={<HourglassMark />}
      title={i.email}
      sub={inviteSub(i)}
      active={selection?.kind === "invite" && selection.email === i.email.toLowerCase()}
      onPress={() => setPicked({ kind: "invite", email: i.email.toLowerCase() })}
    />
  );

  return (
    <SettingsPage>
      <PageHead {...head} title="People" status={status} action={isOwner ? <KitButton onClick={() => setInviteOpen(true)}>Invite</KitButton> : null} />

      {membersQuery.isLoading ? (
        <SplitLoading caption="Loading the team" />
      ) : membersQuery.isError ? (
        <ErrorState
          title="The team did not load"
          body={`Creo could not read who is in ${orgName}. Nobody’s access has changed.`}
          onRetry={() => void membersQuery.refetch()}
        />
      ) : (
        <Split
          backLabel="People"
          index={
            <div className="flex flex-col gap-3">
              {roster > 10 ? (
                <KitInput icon={Search} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search people" aria-label="Search people" />
              ) : null}
              <IndexCard>
                <GroupLabel>{q ? `Team · ${team.length + teamInvites.length} match` : "Team"}</GroupLabel>
                {team.map(memberRow)}
                {teamInvites.map(inviteRow)}
                {clients.length + clientInvites.length > 0 ? (
                  <>
                    <GroupLabel>{q ? `Clients · ${clients.length + clientInvites.length} match` : "Clients"}</GroupLabel>
                    {clients.map(memberRow)}
                    {clientInvites.map(inviteRow)}
                  </>
                ) : null}
              </IndexCard>
            </div>
          }
          detail={
            selectedInvite ? (
              <InviteDetail
                invite={selectedInvite}
                isOwner={isOwner}
                usedSeats={usedSeats}
                maxSeats={maxSeats}
                onRevoke={() => {
                  revokeInvite.mutate(selectedInvite.id);
                  setPicked(null);
                }}
              />
            ) : shownMember ? (
              <MemberDetail
                member={shownMember}
                me={user?.id}
                isOwner={isOwner}
                brandCount={brandCount}
                maxSeats={maxSeats}
                onRole={(role) => changeRole(shownMember, role)}
                onEditBrands={() => setToClient(shownMember)}
                onRemove={() => setRemoveTarget(shownMember)}
              />
            ) : null
          }
        />
      )}

      {inviteOpen && isOwner ? (
        <InviteDialog
          organizationId={orgId}
          organizationName={orgName}
          seatsLeft={seatsLeft}
          maxSeats={maxSeats}
          onClose={() => setInviteOpen(false)}
          onSent={(email) => setPicked({ kind: "invite", email })}
        />
      ) : null}

      {widen ? (
        <ConfirmDialog
          danger={false}
          title={`Make ${firstName(widen.member)} a ${widen.role}?`}
          description={[
            widen.member.role === "Guest"
              ? `${widen.member.name || widen.member.email} will see all ${plural(brandCount, "brand")} instead of ${(widen.member.brands ?? []).length}.`
              : `${widen.member.name || widen.member.email} will be able to plan, write and schedule posts.`,
            widen.member.role === "Guest"
              ? `${firstName(widen.member)} takes a seat${maxSeats != null ? `: ${usedSeats + 1} of ${maxSeats} in use after this` : ""}.`
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
          confirmLabel={`Make ${widen.role}`}
          onClose={() => setWiden(null)}
          onConfirm={() => {
            updateRole.mutate({ userId: widen.member.userId, role: widen.role });
            setWiden(null);
          }}
        />
      ) : null}

      {toClient ? (
        <EditBrandsDialog
          name={firstName(toClient)}
          title={toClient.role === "Guest" ? undefined : `Make ${firstName(toClient)} a Client?`}
          initial={(toClient.brands ?? []).map((b) => b.id)}
          onClose={() => setToClient(null)}
          onSave={(ids) => {
            const picked = (brands ?? []).filter((b) => ids.includes(b.id)).map(({ id, name }) => ({ id, name }));
            if (toClient.role === "Guest") {
              updateRole.mutate({ userId: toClient.userId, role: "Guest", brands: picked });
            } else {
              changeRole(toClient, "Guest", picked);
            }
          }}
        />
      ) : null}

      {removeTarget ? (
        <ConfirmDialog
          title={`Remove ${removeTarget.name || removeTarget.email} from ${orgName}?`}
          description={`${firstName(removeTarget)} loses access at once.`}
          consequences={[
            {
              icon: Store,
              text:
                removeTarget.role === "Guest"
                  ? `${plural((removeTarget.brands ?? []).length, "brand")} close for ${firstName(removeTarget)}.`
                  : `${firstName(removeTarget)} loses all ${plural(brandCount, "brand")}.`,
            },
            ...(takesSeat(removeTarget.role) ? [{ icon: Users, text: "A seat frees up." }] : []),
          ]}
          confirmLabel={`Remove ${firstName(removeTarget)}`}
          onClose={() => setRemoveTarget(null)}
          onConfirm={() => {
            removeMember.mutate(removeTarget.userId);
            setPicked(null);
            setRemoveTarget(null);
          }}
        />
      ) : null}
    </SettingsPage>
  );
}

function RoleMenu({ role, onChange }: { role: string; onChange: (role: AssignableRole) => void }) {
  return (
    <KitMenuTrigger>
      <KitPressable>
        <KitButton variant="secondary" iconRight={ChevronDown}>
          {roleLabel(role)}
        </KitButton>
      </KitPressable>
      <KitMenuPopover>
        <KitMenu
          aria-label="Role"
          selectionMode="single"
          selectedKeys={[role]}
          onAction={(key) => onChange(String(key) as AssignableRole)}
        >
          {ROLE_OPTIONS.map((r) => (
            <KitMenuItem key={r} id={r}>
              {roleLabel(r)}
            </KitMenuItem>
          ))}
        </KitMenu>
      </KitMenuPopover>
    </KitMenuTrigger>
  );
}

function MemberDetail({
  member,
  me,
  isOwner,
  brandCount,
  maxSeats,
  onRole,
  onEditBrands,
  onRemove,
}: {
  member: OrganizationMember;
  me: string | undefined;
  isOwner: boolean;
  brandCount: number;
  maxSeats: number | null;
  onRole: (role: AssignableRole) => void;
  onEditBrands: () => void;
  onRemove: () => void;
}) {
  const isMe = member.userId === me;
  const canManage = isOwner && member.role !== "Owner" && !isMe;
  const isClient = member.role === "Guest";
  const brands = member.brands ?? [];
  const name = member.name || member.email;

  return (
    <DetailCard>
      <DetailHead
        lead={<PersonMark name={name} picture={member.profilePicture} size={56} />}
        title={name}
        sub={member.email}
        aside={
          canManage ? (
            <>
              <RoleMenu role={member.role} onChange={onRole} />
              <KitMenuTrigger>
                <KitPressable>
                  <KitIconButton icon={Ellipsis} aria-label={`More for ${name}`} />
                </KitPressable>
                <KitMenuPopover>
                  <KitMenu aria-label="More" onAction={(key) => key === "remove" && onRemove()}>
                    <KitMenuItem id="remove" icon={UserMinus} danger>
                      Remove from the workspace
                    </KitMenuItem>
                  </KitMenu>
                </KitMenuPopover>
              </KitMenuTrigger>
            </>
          ) : (
            <StatusLabel>{roleLabel(member.role)}</StatusLabel>
          )
        }
      />
      {isClient ? (
        <>
          <Facts>
            <Fact label="Access">
              {brands.length} of {plural(brandCount, "brand")}. A client sees only the brands below, and takes no seat.
            </Fact>
            <Fact label="Last opened">{member.lastSeenAt ? formatMoment(member.lastSeenAt) : "Not yet"}</Fact>
          </Facts>
          <SectionHead title={`Brands ${firstName(member)} can open`} meta={String(brands.length)} />
          <div className="-mx-2 flex flex-col">
            {brands.map((b) => (
              <div key={b.id} className="flex items-center gap-3 rounded-row px-2 py-2">
                <BrandMark brand={b} size={32} />
                <span className="flex flex-col">
                  <span className="text-[13.5px] font-semibold text-co-text">{b.name}</span>
                  <span className="text-[12px] text-co-muted">Calendar and concepts, for review</span>
                </span>
              </div>
            ))}
          </div>
          {canManage ? <TextAction onPress={onEditBrands}>Add a brand</TextAction> : null}
        </>
      ) : (
        <Facts>
          <Fact label="Can">{roleCan(member.role, brandCount)}</Fact>
          <Fact label="Seat">{maxSeats != null ? `Takes 1 of the ${maxSeats} seats` : "Takes a seat"}</Fact>
          <Fact label="Last active">{member.lastSeenAt ? formatMoment(member.lastSeenAt) : "Not yet"}</Fact>
        </Facts>
      )}
      {isMe && member.role === "Owner" ? <QuietNote>You own this workspace. An owner cannot be removed.</QuietNote> : null}
    </DetailCard>
  );
}

function InviteDetail({
  invite,
  isOwner,
  usedSeats,
  maxSeats,
  onRevoke,
}: {
  invite: OrganizationInviteSummary;
  isOwner: boolean;
  usedSeats: number;
  maxSeats: number | null;
  onRevoke: () => void;
}) {
  const who = invite.email.split("@")[0];
  const isClient = invite.role === "Guest";
  return (
    <DetailCard>
      <DetailHead
        lead={<PersonMark name={invite.email} size={56} />}
        title={invite.email}
        sub={`Invited as ${isClient ? "a Client" : `a ${invite.role}`}`}
        aside={
          <>
            <StatusLabel icon={Hourglass}>Waiting for {who}</StatusLabel>
            {isOwner ? (
              <KitButton variant="secondary" onClick={onRevoke}>
                Revoke
              </KitButton>
            ) : null}
          </>
        }
      />
      <Facts>
        <Fact label="Sent by">
          {[invite.inviter.name || invite.inviter.email, formatMoment(invite.createdAt).toLowerCase()].filter(Boolean).join(", ")}
        </Fact>
        <Fact label="Expires">{formatDay(invite.expiresAt)}</Fact>
        {isClient ? (
          <Fact label="Brands">{(invite.brands ?? []).map((b) => b.name).join(", ") || "None"}</Fact>
        ) : null}
        <Fact label={`When ${who} joins`}>
          {isClient
            ? "Takes no seat"
            : `${who} takes the seat held for them${maxSeats != null ? `: ${usedSeats} of ${maxSeats} in use` : ""}`}
        </Fact>
      </Facts>
    </DetailCard>
  );
}
